/**
 * Success screen (plan 6.I) — replaces the main column after publish.
 */

import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import octoIcon from "../../../../assets/icons/octo_icon.svg";
import { FRONTEND_BASE_URL } from "../../../../utils/utility";
import Icon from "../ui/Icon";
import { useBuilder } from "../state/builderContext";
import { actions } from "../state/builderReducer";
import { COPY, E, KIND, SECTIONS } from "../state/constants";
import { durationMinutes, inviteeChoosesDuration, isGroup, priceLabel, selectedTemplate, venueName } from "../state/selectors";

export const copyText = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const EventSuccess = ({ wasEdit }) => {
  const { state, dispatch, businessId, toast, goToSection } = useBuilder();
  const userPlan = useSelector((s) => (s.subscription || {}).userPlan) || {};
  const paid = state.meta.publishedKind === KIND.PAID || state.meta.kind === KIND.PAID;
  const inactive = state.meta.isActive !== E;
  const link = `${FRONTEND_BASE_URL}booking/${businessId}/${state.type.event_url}`;
  const copiedOnce = useRef(false);

  useEffect(() => {
    if (copiedOnce.current) return;
    copiedOnce.current = true;
    copyText(link).then((ok) => ok && toast(COPY.toast.linkCopied));
    try {
      window.scrollTo({ top: 0 });
    } catch (e) {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eyebrow = inactive ? "EVENT CREATED \u00B7 INACTIVE" : wasEdit ? "EVENT SAVED" : paid ? "PAID EVENT CREATED" : "FREE EVENT CREATED";
  const title = inactive ? "Your event is saved but inactive" : "Your event is ready to share";
  const body = inactive
    ? "Your plan allows one active event. Deactivate another event to make this one live."
    : paid
    ? `Invitees will pay ${priceLabel(state)} when they book.`
    : "Share the link and invitees can book right away.";
  const durationText = inviteeChoosesDuration(state) ? "Invitee chooses" : `${durationMinutes(state)} minutes`;
  const formatText = isGroup(state) ? `Group \u00B7 ${state.type.no_of_attendies} invitees` : "One-to-one";
  const template = selectedTemplate(state);
  // free-plan "one active event" copy only makes sense on that plan; the pill always tells the truth
  const planNote = inactive && String(userPlan.plan_id) === "1";

  return (
    <div className="eb-event-success">
      <div className="eb-success-mark">
        <Icon name="check" size={19} />
      </div>
      <span className="eb-success-eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{planNote || !inactive ? body : "This event is inactive. You can activate it from the events list."}</p>

      <section className="eb-created-card" style={{ borderTop: "4px solid var(--event-accent)" }}>
        <div className="eb-created-card-top">
          <div className="eb-created-logo">
            <img src={octoIcon} alt="" />
          </div>
          <h2>{state.type.event_name}</h2>
          <span>
            {durationText}
            {" \u00B7 "}
            {formatText}
            {venueName(state) ? ` \u00B7 ${venueName(state)}` : ""}
          </span>
          <span className="eb-live-pill">
            <i />
            {inactive ? "Inactive" : "Live"}
          </span>
        </div>
        <div className="eb-created-details">
          <div>
            <Icon name="calendar" size={17} />
            <span>
              <small>Availability</small>
              <strong>{template ? template.label : "Custom hours"}</strong>
            </span>
          </div>
          {paid ? (
            <div>
              <Icon name="card" size={17} />
              <span>
                <small>Price</small>
                <strong>{priceLabel(state)}</strong>
              </span>
            </div>
          ) : (
            <div>
              <Icon name="clock" size={17} />
              <span>
                <small>Duration</small>
                <strong>{inviteeChoosesDuration(state) ? "Custom" : `${durationMinutes(state)} min`}</strong>
              </span>
            </div>
          )}
        </div>
        <div className="eb-link-box">
          <Icon name="link" size={17} />
          <span>{link}</span>
          <button type="button" onClick={() => copyText(link).then((ok) => ok && toast(COPY.toast.copied))}>
            <Icon name="copy" size={14} /> Copy
          </button>
        </div>
      </section>

      <div className="eb-success-actions">
        <button type="button" className="eb-success-primary" onClick={() => copyText(link).then((ok) => ok && toast(COPY.toast.linkCopied))}>
          <Icon name="copy" size={17} /> Copy booking link
        </button>
        <button type="button" className="eb-success-secondary" onClick={() => window.open(link, "_blank", "noopener")}>
          <Icon name="eye" size={17} /> View booking page
        </button>
      </div>

      <button
        type="button"
        className="eb-customize-link"
        onClick={() => {
          dispatch(actions.setMeta({ published: false }));
          goToSection(SECTIONS.QUICK);
        }}
      >
        Continue customizing event settings <Icon name="chevron" size={15} />
      </button>
    </div>
  );
};

export default EventSuccess;
